import React, { useState, useMemo } from 'react';
import { X, Code2, Eye, Copy, Download, CheckCircle2 } from 'lucide-react';
import DynamicFormRenderer from './DynamicFormRenderer';
import { generateJsonSchema, MODULE_SCHEMAS } from '../data/masterData';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export default function ViewMappingDialog({ open, moduleKey, rows, meta, onClose }) {
  const [tab, setTab] = useState('schema');
  const [copied, setCopied] = useState(false);

  const schema = useMemo(() => {
    if (!moduleKey) return null;
    return generateJsonSchema(moduleKey, meta?.columnMap, meta?.headers, rows || [], meta?.version);
  }, [moduleKey, rows, meta]);

  if (!open || !schema) return null;
  const moduleSchema = MODULE_SCHEMAS[moduleKey];

  const jsonText = JSON.stringify(schema, null, 2);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      setCopied(true);
      toast.success('JSON Schema copied to clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy schema');
    }
  };

  const download = () => {
    const blob = new Blob([jsonText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${moduleKey}_schema.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('schema.json downloaded');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="view-mapping-dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[94vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: moduleSchema.color + '15', color: moduleSchema.color }}
            >
              <Code2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">{moduleSchema.label} — Schema & Form</h2>
              <p className="text-xs text-slate-500">
                Generated JSON schema from column mapping · drives all dynamic forms
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"
            data-testid="view-mapping-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Meta strip */}
        <div className="px-6 py-3 bg-slate-50/60 border-b border-slate-100 grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Module" value={schema.module} />
          <Stat label="Version" value={schema.version.startsWith('v') ? schema.version : `v${schema.version}`} />
          <Stat label="Sections" value={schema.sections.length} />
          <Stat label="Records" value={schema.rowCount} />
          <Stat label="Source File" value={meta?.fileName || '—'} mono />
        </div>

        {/* Tabs */}
        <div className="px-6 pt-3 border-b border-slate-100 flex gap-1">
          <TabBtn active={tab === 'schema'} onClick={() => setTab('schema')} icon={Code2} label="JSON Schema" testId="tab-schema" />
          <TabBtn active={tab === 'form'} onClick={() => setTab('form')} icon={Eye} label="Live Form Preview" testId="tab-form" />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/40">
          {tab === 'schema' && (
            <div data-testid="schema-json-panel">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs text-slate-600">
                  This schema is the contract HGS forms render against. Each section maps to a classification group; each field defines type, validation, and source Excel column.
                </p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={copy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-[11px] font-semibold rounded-md transition"
                    data-testid="copy-schema-btn"
                  >
                    {copied ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    onClick={download}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-semibold rounded-md transition"
                    data-testid="download-schema-btn"
                  >
                    <Download className="w-3 h-3" />
                    Download .json
                  </button>
                </div>
              </div>
              <pre className="p-4 rounded-xl bg-slate-900 text-slate-100 text-[11px] leading-relaxed overflow-auto max-h-[60vh] font-mono">
                {jsonText}
              </pre>
            </div>
          )}

          {tab === 'form' && <DynamicFormRenderer schema={schema} />}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 bg-white flex items-center justify-between">
          <div className="text-[11px] text-slate-500">
            Generated at <span className="font-mono">{new Date(schema.generatedAt).toLocaleString()}</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg transition"
            data-testid="view-mapping-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={cn('text-xs font-bold text-slate-900 truncate mt-0.5', mono && 'font-mono')} title={String(value)}>
        {value}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-t-lg border-b-2 transition',
        active ? 'text-[#CC0000] border-[#CC0000] bg-red-50/40' : 'border-transparent text-slate-600 hover:text-slate-900'
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}
